/**
 * Working out who can see what, from the grant points the API returns.
 *
 * The API sends only grants made ON a card. Inheritance is derived here by
 * walking up from a card to the nearest grant above it, which keeps the payload
 * to the handful of interesting rows rather than one row per (principal, card).
 */
import type { AccessCard, AccessGrant, AccessOverviewResponse, AccessPrincipal } from './api/types';
import type { DashboardPermission } from './api/types';

/**
 * The two organisation-kind principals are not the same audience: one is this
 * tenant, the other is every account on the platform. The backend sends this
 * literal as the id of the second (see _organisation_grant in dashboards
 * service.py), so copy that describes an audience has to tell them apart.
 */
export const ALL_USERS_PRINCIPAL_ID = 'all-users';

/** Role ladder, lowest first. Values are ResourcePermissionRole enum values. */
export const ROLE_LADDER: { value: DashboardPermission; label: string }[] = [
  { value: 10, label: 'View' },
  { value: 20, label: 'Comment' },
  { value: 25, label: 'Data Entry' },
  { value: 30, label: 'Edit' },
  { value: 40, label: 'Share' },
  { value: 50, label: 'Owner' }
];

export function roleLabel(role: DashboardPermission): string {
  return ROLE_LADDER.find((r) => r.value === role)?.label ?? String(role);
}

export interface AccessTreeNode {
  card: AccessCard;
  depth: number;
  hasChildren: boolean;
}

/**
 * The subtree flattened in display order, parents before their children.
 *
 * The root is the card whose parent_id is null — the backend nulls it for the
 * node the overview was opened on, so a group card opened directly is its own
 * root and nothing above it appears.
 */
export function flattenCards(cards: AccessCard[], expanded: Set<string>): AccessTreeNode[] {
  const childrenOf = new Map<string | null, AccessCard[]>();
  for (const card of cards) {
    const siblings = childrenOf.get(card.parent_id) ?? [];
    siblings.push(card);
    childrenOf.set(card.parent_id, siblings);
  }

  const out: AccessTreeNode[] = [];
  const walk = (parentId: string | null, depth: number): void => {
    for (const card of childrenOf.get(parentId) ?? []) {
      const children = childrenOf.get(card.id) ?? [];
      out.push({ card, depth, hasChildren: children.length > 0 });
      if (children.length > 0 && expanded.has(card.id)) {
        walk(card.id, depth + 1);
      }
    }
  };
  walk(null, 0);
  return out;
}

/** Every card from `cardId` up to the root of the returned subtree, nearest first. */
function pathToRoot(cardId: string, byId: Map<string, AccessCard>): string[] {
  const path: string[] = [];
  let current: string | null = cardId;
  while (current) {
    path.push(current);
    current = byId.get(current)?.parent_id ?? null;
  }
  return path;
}

export interface EffectiveAccess {
  role: DashboardPermission;
  /** The card the winning grant was made on. */
  grantedOn: string;
  /** True when the grant was made on this very card, so it can be changed here. */
  grantedHere: boolean;
  /** True when the grant was made above the subtree, so there is no card here to open. */
  fromAbove: boolean;
}

/**
 * The highest level these principals reach on this card.
 *
 * Several principals are passed together when tracing a person: their own
 * grants, every group they are in, and the organisation-wide share. Their
 * effective access is the maximum across all of them, so a trace built from
 * personal grants alone would understate what they can see.
 */
export function effectiveAccess(
  cardId: string,
  principalIds: Set<string>,
  grants: AccessGrant[],
  byId: Map<string, AccessCard>
): EffectiveAccess | null {
  const path = pathToRoot(cardId, byId);
  let best: EffectiveAccess | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const grant of grants) {
    if (!principalIds.has(grant.principal.id)) continue;
    // pathToRoot is ordered nearest-first, so the index is the distance up the
    // tree; -1 means the grant is on a card that is not an ancestor of this one.
    const distance = path.indexOf(grant.card_id);
    if (distance === -1) continue;

    // A higher level always wins. On a tie the NEARER grant wins, so a grant
    // made on this card is never reported as inherited from an ancestor that
    // happens to sit at the same level — the pill would read "inherited" and
    // the row would look like there was nothing here to change. Two grants at
    // the same distance are the root's own and one from above it; the one that
    // can be changed here wins, for the same reason.
    const better =
      !best ||
      grant.role > best.role ||
      (grant.role === best.role &&
        (distance < bestDistance || (distance === bestDistance && best.fromAbove && !grant.from_above)));
    if (!better) continue;

    best = {
      role: grant.role,
      grantedOn: grant.card_id,
      // A row flagged from_above sits on this card but was granted off the top
      // of the subtree, so it is inherited however close it looks.
      grantedHere: grant.card_id === cardId && !grant.from_above,
      fromAbove: grant.from_above
    };
    bestDistance = distance;
  }
  return best;
}

/**
 * Everything that supplies a principal's access: itself, plus — for a person —
 * the groups they belong to and the organisation-wide share.
 */
export function sourcesOf(principal: AccessPrincipal, grants: AccessGrant[]): Set<string> {
  const sources = new Set<string>([principal.id]);
  if (principal.kind !== 'person') return sources;

  for (const groupId of principal.member_of) sources.add(groupId);
  for (const grant of grants) {
    if (grant.principal.kind === 'organisation') sources.add(grant.principal.id);
  }
  return sources;
}

/** One entry per principal holding a grant anywhere in the subtree, deduplicated. */
export function principalsInPlay(grants: AccessGrant[]): AccessPrincipal[] {
  const byKey = new Map<string, AccessPrincipal>();
  for (const grant of grants) {
    const existing = byKey.get(grant.principal.id);
    // Keep whichever copy knows about the most groups: member_of is filled from
    // the payload as a whole, but a given row may predate that.
    if (!existing || grant.principal.member_of.length > existing.member_of.length) {
      byKey.set(grant.principal.id, grant.principal);
    }
  }
  const order: Record<AccessPrincipal['kind'], number> = { organisation: 0, group: 1, person: 2 };
  return [...byKey.values()].sort((a, b) => order[a.kind] - order[b.kind] || a.name.localeCompare(b.name));
}

export interface CardAccessSummary {
  /** Principals reaching this card, highest level first. */
  holders: { principal: AccessPrincipal; access: EffectiveAccess }[];
  /** The highest level anyone reaches here. */
  topRole: DashboardPermission | null;
  /** True when at least one of them was granted on this card rather than above. */
  grantedHere: boolean;
}

/** Who reaches one card, for the everyone-view. */
export function summariseCard(
  cardId: string,
  principals: AccessPrincipal[],
  grants: AccessGrant[],
  byId: Map<string, AccessCard>
): CardAccessSummary {
  const holders: { principal: AccessPrincipal; access: EffectiveAccess }[] = [];
  for (const principal of principals) {
    // Each principal is summarised as the table stores it — one row per
    // grant-holder. Folding a person's groups in here would count the same
    // access twice, once for them and once for the group.
    const access = effectiveAccess(cardId, new Set([principal.id]), grants, byId);
    if (access) holders.push({ principal, access });
  }
  holders.sort((a, b) => b.access.role - a.access.role);

  return {
    holders,
    topRole: holders.length > 0 ? holders[0].access.role : null,
    grantedHere: holders.some((h) => h.access.grantedHere)
  };
}

export interface LevelSummary {
  /** One level when everyone here holds the same, otherwise the range. */
  label: string;
  /** Every level present with how many hold it, most privileged first. */
  breakdown: string;
  /** True when the holders do not all hold the same level. */
  varies: boolean;
}

/**
 * What the levels on one card add up to.
 *
 * A single level across everyone is worth naming. Where they differ, the
 * maximum on its own is close to useless — creating a dashboard writes an OWNER
 * row for its creator and every card below inherits it, so "the highest anyone
 * holds" is Owner on essentially every row. The range says the thing the
 * maximum hides: that these people do not all have the same access, and the row
 * is worth opening.
 */
export function summariseLevels(holders: { access: { role: DashboardPermission } }[]): LevelSummary | null {
  if (holders.length === 0) return null;

  const counts = new Map<DashboardPermission, number>();
  for (const holder of holders) {
    counts.set(holder.access.role, (counts.get(holder.access.role) ?? 0) + 1);
  }
  const levels = [...counts.keys()].sort((a, b) => a - b);
  const lowest = levels[0];
  const highest = levels[levels.length - 1];

  return {
    label: levels.length === 1 ? roleLabel(lowest) : `${roleLabel(lowest)} → ${roleLabel(highest)}`,
    breakdown: [...levels]
      .reverse()
      .map((level) => `${counts.get(level)} × ${roleLabel(level)}`)
      .join(', '),
    varies: levels.length > 1
  };
}

/** The cards that should start expanded: the root and its immediate children. */
export function defaultExpanded(response: AccessOverviewResponse): Set<string> {
  const expanded = new Set<string>();
  for (const card of response.cards) {
    if (card.parent_id === null) expanded.add(card.id);
  }
  return expanded;
}

export function cardsById(cards: AccessCard[]): Map<string, AccessCard> {
  return new Map(cards.map((card) => [card.id, card]));
}
